import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { db } from '../lib/firebase';
import { collection, addDoc, serverTimestamp, getDocs, query, where, doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import SmartQuoteWizard from '../components/SmartQuoteWizard';

export default function NewProjectPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Calculate default dates
  const today = new Date().toISOString().split('T')[0];
  const threeMonthsLater = new Date();
  threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
  const defaultEndDate = threeMonthsLater.toISOString().split('T')[0];
  
  const [formData, setFormData] = useState({
    name: '',
    briefDescription: '',
    startDate: today,
    estimatedEndDate: defaultEndDate,
    customer: null,
    components: []
  });
  
  const [newComponent, setNewComponent] = useState({
    category: 'upholstery'
  });
  
  const [showComponentWizard, setShowComponentWizard] = useState(false);
  const [showComponentNamePopup, setShowComponentNamePopup] = useState(false);
  const [componentName, setComponentName] = useState('');
  const [currentComponentIndex, setCurrentComponentIndex] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddingComponent, setIsAddingComponent] = useState(false);
  const [currentComponentId, setCurrentComponentId] = useState(null);
  const [draftProjectId, setDraftProjectId] = useState(null);
  const [error, setError] = useState('');
  
  // Customer-related state
  const [customers, setCustomers] = useState([]);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    address: ''
  });
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // Load customers function
  const loadCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const customersSnapshot = await getDocs(collection(db, 'customers'));
      const customersData = customersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCustomers(customersData);
    } catch (error) {
      console.error('Error loading customers:', error);
      setError('Failed to load customers');
    } finally {
      setLoadingCustomers(false);
    }
  };

  // Handle new customer creation
  const handleCreateNewCustomer = async () => {
    try {
      if (!newCustomer.name.trim()) {
        setError('Customer name is required');
        return;
      }

      const customerData = {
        name: newCustomer.name.trim(),
        email: newCustomer.email.trim() || null,
        phone: newCustomer.phone.trim() || null,
        company: newCustomer.company.trim() || null,
        address: newCustomer.address.trim() || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'customers'), customerData);
      
      const createdCustomer = {
        id: docRef.id,
        ...customerData
      };

      setCustomers(prev => [...prev, createdCustomer]);
      setFormData(prev => ({ ...prev, customer: createdCustomer }));
      setShowNewCustomerForm(false);
      setNewCustomer({
        name: '',
        email: '',
        phone: '',
        company: '',
        address: ''
      });
      setError('');
    } catch (error) {
      console.error('Error creating customer:', error);
      setError('Failed to create customer');
    }
  };

  // Load customers on component mount
  useEffect(() => {
    loadCustomers();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const handleCustomerSelect = (customer) => {
    setFormData(prev => ({ ...prev, customer }));
    setShowNewCustomerForm(false);
  };

  const handleNewCustomerChange = (e) => {
    const { name, value } = e.target;
    setNewCustomer(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const startComponentNameEntry = () => {
    setError('');
    setComponentName('');
    setShowComponentNamePopup(true);
  };

  const handleComponentNameSubmit = async () => {
    if (!componentName.trim()) {
      setError('Please enter a component name');
      return;
    }

    setIsAddingComponent(true);
    
    const newComponentId = Date.now();
    setCurrentComponentId(newComponentId);
    
    let actualProjectId = draftProjectId;
    
    // Create draft project if it doesn't exist
    if (!actualProjectId) {
      try {
        const draftProjectData = {
          name: 'Draft Project',
          briefDescription: 'Draft for component creation',
          status: 'draft',
          createdBy: user.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        const docRef = await addDoc(collection(db, 'projects'), draftProjectData);
        actualProjectId = docRef.id;
        setDraftProjectId(actualProjectId);
        console.log('Draft project created with ID:', actualProjectId);
      } catch (error) {
        console.error('Error creating draft project:', error);
        setError('Failed to create draft project');
        setIsAddingComponent(false);
        return;
      }
    }
    
    console.log('=== ABOUT TO OPEN WIZARD ===');
    console.log('actualProjectId:', actualProjectId);
    console.log('newComponentId:', newComponentId);
    console.log('componentName:', componentName);
    console.log('draftProjectId state:', draftProjectId);
    
    // Close name popup and open wizard
    setShowComponentNamePopup(false);
    setShowComponentWizard(true);
    setIsAddingComponent(false);
  };

  const startComponentWizard = async () => {
    setError('');
    setIsAddingComponent(true);
    
    const newComponentId = Date.now();
    setCurrentComponentId(newComponentId);
    
    let actualProjectId = draftProjectId;
    
    // Create draft project if it doesn't exist
    if (!actualProjectId) {
      try {
        const draftProjectData = {
          name: 'Draft Project',
          briefDescription: 'Draft for component creation',
          status: 'draft',
          createdBy: user.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        const docRef = await addDoc(collection(db, 'projects'), draftProjectData);
        actualProjectId = docRef.id;
        setDraftProjectId(actualProjectId);
        console.log('Draft project created with ID:', actualProjectId);
      } catch (error) {
        console.error('Error creating draft project:', error);
        setError('Failed to create draft project');
        setIsAddingComponent(false);
        return;
      }
    }
    
    console.log('=== ABOUT TO OPEN WIZARD ===');
    console.log('actualProjectId:', actualProjectId);
    console.log('newComponentId:', newComponentId);
    console.log('draftProjectId state:', draftProjectId);
    setShowComponentWizard(true);
    setIsAddingComponent(false);
  };

  // Helper function to upload component photos to Firebase Storage
  const uploadComponentPhotos = async (photos, projectId, componentId) => {
    const uploadPromises = photos.map(async (file, index) => {
      const fileName = `component_${Date.now()}_${index}_${file.name}`;
      const path = `projects/${projectId}/components/${componentId}/${fileName}`;
      
      const storageRef = ref(storage, path);
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          null,
          (error) => reject(error),
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve({
                url: downloadURL,
                fileName: file.name,
                path,
                uploadedAt: new Date()
              });
            } catch (error) {
              reject(error);
            }
          }
        );
      });
    });
    
    return Promise.all(uploadPromises);
  };

  // Helper function to upload fabric photos to Firebase Storage
  const uploadFabricPhotos = async (photos, projectId, componentId) => {
    const uploadPromises = photos.map(async (file, index) => {
      const fileName = `fabric_${Date.now()}_${index}_${file.name}`;
      const path = `projects/${projectId}/components/${componentId}/fabric/${fileName}`;
      
      const storageRef = ref(storage, path);
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          null,
          (error) => reject(error),
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve({
                url: downloadURL,
                fileName: file.name,
                path,
                uploadedAt: new Date()
              });
            } catch (error) {
              reject(error);
            }
          }
        );
      });
    });
    
    return Promise.all(uploadPromises);
  };

  const handleComponentWizardComplete = async (quoteData) => {
    console.log('=== Component wizard complete ===');
    console.log('Raw quoteData:', quoteData);
    console.log('uploadedFabricUrls:', quoteData.uploadedFabricUrls);
    console.log('uploadedComponentUrls:', quoteData.uploadedComponentUrls);
    
    // Since images are already uploaded during the wizard, store the URLs
    const cleanFabric = quoteData.fabric ? {
      description: quoteData.fabric.description || '',
      imageUrls: quoteData.uploadedFabricUrls || [],
      imageCount: quoteData.uploadedFabricUrls ? quoteData.uploadedFabricUrls.length : 0
    } : null;

    const cleanComponentPhotos = quoteData.uploadedComponentUrls ? {
      imageUrls: quoteData.uploadedComponentUrls || [],
      imageCount: quoteData.uploadedComponentUrls.length
    } : null;
    
    console.log('cleanFabric:', cleanFabric);
    console.log('cleanComponentPhotos:', cleanComponentPhotos);

    const componentId = currentComponentId;
    const component = {
      id: componentId,
      name: quoteData.componentName.trim(),
      category: newComponent.category,
      // Images already uploaded - no pending photos
      pendingPhotos: [],
      pendingFabricPhotos: [],
      // Store complete taxonomy data (cleaned for Firestore)
      taxonomyData: {
        product: quoteData.product,
        category: quoteData.category,
        subcategory: quoteData.subcategory,
        features: quoteData.features || [],
        additionals: quoteData.additionals || [],
        dimensions: quoteData.dimensions || {},
        fabric: cleanFabric,
        componentPhotos: cleanComponentPhotos,
        materials: quoteData.materials || [],
        description: quoteData.description || '',
        generatedDescription: quoteData.generatedDescription || ''
      }
    };

    setFormData(prev => ({
      ...prev,
      components: [...prev.components, component]
    }));

    // Reset component form
    setNewComponent({
      category: 'upholstery'
    });
    
    setShowComponentWizard(false);
    setCurrentComponentId(null);
    setError('');
  };

  const handleComponentNameCancel = () => {
    setShowComponentNamePopup(false);
    setComponentName('');
    setError('');
  };

  const handleComponentWizardCancel = () => {
    setShowComponentWizard(false);
    setIsAddingComponent(false);
    setCurrentComponentId(null);
    setComponentName('');
  };

  const removeComponent = (componentId) => {
    setFormData(prev => ({
      ...prev,
      components: prev.components.filter(c => c.id !== componentId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Basic validation
      if (!formData.name.trim()) {
        setError('Please enter a project name');
        setIsSubmitting(false);
        return;
      }

      if (!formData.customer) {
        setError('Please select or create a customer');
        setIsSubmitting(false);
        return;
      }

      if (!formData.briefDescription.trim()) {
        setError('Please enter a brief description');
        setIsSubmitting(false);
        return;
      }

      // Clean components data for Firestore (remove pending photos)
      const cleanComponents = formData.components.map(component => {
        const { pendingPhotos, pendingFabricPhotos, ...cleanComponent } = component;
        return cleanComponent;
      });

      // Prepare project data
      const projectData = {
        name: formData.name.trim(),
        briefDescription: formData.briefDescription.trim(),
        startDate: formData.startDate ? new Date(formData.startDate) : new Date(),
        estimatedEndDate: formData.estimatedEndDate ? new Date(formData.estimatedEndDate) : null,
        customer: formData.customer,
        components: cleanComponents,
        status: 'planning',
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Update draft project or create new one
      let projectId;
      if (draftProjectId) {
        // Update existing draft project
        try {
          const projectRef = doc(db, 'projects', draftProjectId);
          await updateDoc(projectRef, projectData);
          projectId = draftProjectId;
          console.log('✅ Draft project updated with ID:', projectId);
          
          // Verify the update worked
          const updatedDoc = await getDoc(projectRef);
          if (updatedDoc.exists()) {
            console.log('✅ Project update verified - document exists');
          } else {
            console.error('❌ Project update failed - document does not exist after update');
          }
        } catch (error) {
          console.error('❌ Error updating draft project:', error);
          throw error;
        }
      } else {
        // Create new project if no draft exists
        try {
          const docRef = await addDoc(collection(db, 'projects'), projectData);
          projectId = docRef.id;
          console.log('✅ New project created with ID:', projectId);
          
          // Verify the creation worked
          const createdDoc = await getDoc(docRef);
          if (createdDoc.exists()) {
            console.log('✅ Project creation verified - document exists');
          } else {
            console.error('❌ Project creation failed - document does not exist after creation');
          }
        } catch (error) {
          console.error('❌ Error creating new project:', error);
          throw error;
        }
      }

      // Upload photos after project creation
      try {
        const allUploadPromises = [];

        // Upload component photos
        const componentPhotoPromises = formData.components
          .filter(component => component.pendingPhotos && component.pendingPhotos.length > 0)
          .map(async (component) => {
            const uploadedPhotos = await uploadComponentPhotos(component.pendingPhotos, projectId, component.id);
            
            // Save photo metadata to componentPhotos collection
            const photoDocPromises = uploadedPhotos.map(photo => 
              addDoc(collection(db, 'componentPhotos'), {
                projectId,
                componentId: component.id,
                componentName: component.name,
                url: photo.url,
                fileName: photo.fileName,
                path: photo.path,
                uploadedAt: serverTimestamp()
              })
            );
            
            return Promise.all(photoDocPromises);
          });
        
        allUploadPromises.push(...componentPhotoPromises);

        // Upload fabric photos
        const fabricPhotoPromises = formData.components
          .filter(component => component.pendingFabricPhotos && component.pendingFabricPhotos.length > 0)
          .map(async (component) => {
            const uploadedPhotos = await uploadFabricPhotos(component.pendingFabricPhotos, projectId, component.id);
            
            // Save photo metadata to fabricPhotos collection
            const photoDocPromises = uploadedPhotos.map(photo => 
              addDoc(collection(db, 'fabricPhotos'), {
                projectId,
                componentId: component.id,
                componentName: component.name,
                url: photo.url,
                fileName: photo.fileName,
                path: photo.path,
                uploadedAt: serverTimestamp()
              })
            );
            
            return Promise.all(photoDocPromises);
          });
        
        allUploadPromises.push(...fabricPhotoPromises);
        
        await Promise.all(allUploadPromises);
        console.log('All photos uploaded successfully');
      } catch (error) {
        console.error('Error uploading photos:', error);
        // Don't fail the project creation if photo upload fails
        alert('Project created successfully, but some photos failed to upload. You can upload them later from the project page.');
      }
      
      // Navigate to the new project
      navigate(`/projects/${projectId}`);
      
    } catch (error) {
      console.error('Error creating project:', error);
      setError('Failed to create project. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppLayout>
      {/* Project Creation Loading Animation */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
            <div className="mb-6">
              <div className="mx-auto w-20 h-20 relative">
                {/* Dancing Flamingo Animation */}
                <div className="w-20 h-20 bg-gradient-to-br from-pink-400 to-pink-600 rounded-full flex items-center justify-center relative overflow-hidden">
                  <div className="flamingo-dance">
                    <span className="text-white text-3xl">🦩</span>
                  </div>
                </div>
                {/* Pulsing ring */}
                <div className="absolute inset-0 border-4 border-pink-200 rounded-full animate-ping opacity-20"></div>
                <div className="absolute inset-0 border-4 border-pink-300 border-t-pink-500 rounded-full animate-spin"></div>
              </div>
            </div>

            {/* Add dancing flamingo CSS */}
            <style jsx>{`
              .flamingo-dance {
                animation: dance 2s ease-in-out infinite;
                transform-origin: center;
              }
              
              @keyframes dance {
                0%, 100% { 
                  transform: rotate(-5deg) scale(1) translateY(0px);
                }
                25% { 
                  transform: rotate(5deg) scale(1.1) translateY(-3px);
                }
                50% { 
                  transform: rotate(-3deg) scale(1.05) translateY(-5px);
                }
                75% { 
                  transform: rotate(3deg) scale(1.1) translateY(-2px);
                }
              }
            `}</style>
            
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Creating Your Project</h3>
            <p className="text-gray-600 mb-4">Please wait while we set up everything...</p>
            
            {/* Progress steps */}
            <div className="space-y-2 text-sm text-left">
              <div className="flex items-center text-green-600">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Saving project details
              </div>
              <div className="flex items-center text-pink-600 animate-pulse">
                <div className="w-4 h-4 mr-2 border-2 border-pink-600 border-t-transparent rounded-full animate-spin"></div>
                Uploading photos
              </div>
              <div className="flex items-center text-gray-400">
                <div className="w-4 h-4 mr-2 border-2 border-gray-300 rounded-full"></div>
                Finalizing setup
              </div>
            </div>
          </div>
        </div>
      )}


      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create New Project</h1>
          <p className="text-gray-600">Enter the basic project information to get started</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Project Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Project Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                placeholder="e.g., Smith Residence - Living Room Renovation"
              />
            </div>

            {/* Customer Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer *
              </label>
              
              {formData.customer ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-green-900">{formData.customer.name}</div>
                      {formData.customer.company && (
                        <div className="text-sm text-green-700">{formData.customer.company}</div>
                      )}
                      {formData.customer.email && (
                        <div className="text-sm text-green-600">{formData.customer.email}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, customer: null }))}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {!showNewCustomerForm ? (
                    <>
                      <div>
                        <label className="block text-sm text-gray-600 mb-2">Select existing customer:</label>
                        {loadingCustomers ? (
                          <div className="text-center py-4">Loading customers...</div>
                        ) : customers.length > 0 ? (
                          <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md">
                            {customers.map((customer) => (
                              <button
                                key={customer.id}
                                type="button"
                                onClick={() => handleCustomerSelect(customer)}
                                className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                              >
                                <div className="font-medium">{customer.name}</div>
                                {customer.company && (
                                  <div className="text-sm text-gray-500">{customer.company}</div>
                                )}
                                {customer.email && (
                                  <div className="text-sm text-gray-400">{customer.email}</div>
                                )}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-4 text-gray-500">No customers found</div>
                        )}
                      </div>
                      
                      <div className="text-center">
                        <span className="text-sm text-gray-500">or</span>
                      </div>
                      
                      <button
                        type="button"
                        onClick={() => setShowNewCustomerForm(true)}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      >
                        Add New Customer
                      </button>
                    </>
                  ) : (
                    <div className="border border-gray-300 rounded-lg p-4 space-y-4">
                      <h4 className="font-medium text-gray-900">Add New Customer</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">Name *</label>
                          <input
                            type="text"
                            name="name"
                            value={newCustomer.name}
                            onChange={handleNewCustomerChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Customer name"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">Company</label>
                          <input
                            type="text"
                            name="company"
                            value={newCustomer.company}
                            onChange={handleNewCustomerChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Company name"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">Email</label>
                          <input
                            type="email"
                            name="email"
                            value={newCustomer.email}
                            onChange={handleNewCustomerChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="email@example.com"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">Phone</label>
                          <input
                            type="tel"
                            name="phone"
                            value={newCustomer.phone}
                            onChange={handleNewCustomerChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Phone number"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Address</label>
                        <textarea
                          name="address"
                          value={newCustomer.address}
                          onChange={handleNewCustomerChange}
                          rows="2"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Customer address"
                        />
                      </div>
                      
                      <div className="flex space-x-3">
                        <button
                          type="button"
                          onClick={handleCreateNewCustomer}
                          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                        >
                          Create Customer
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowNewCustomerForm(false);
                            setNewCustomer({ name: '', email: '', phone: '', company: '', address: '' });
                          }}
                          className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Brief Description */}
            <div>
              <label htmlFor="briefDescription" className="block text-sm font-medium text-gray-700 mb-1">
                Brief Project Description *
              </label>
              <textarea
                id="briefDescription"
                name="briefDescription"
                required
                rows={3}
                value={formData.briefDescription}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                placeholder="e.g., Complete living room makeover including new sofa, curtains, and lighting fixtures"
              />
            </div>

            {/* Date Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date *
                </label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  required
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                />
                <p className="text-xs text-gray-500 mt-1">Default: Today</p>
              </div>

              <div>
                <label htmlFor="estimatedEndDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Estimated End Date *
                </label>
                <input
                  type="date"
                  id="estimatedEndDate"
                  name="estimatedEndDate"
                  required
                  value={formData.estimatedEndDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                />
                <p className="text-xs text-gray-500 mt-1">Default: 3 months from start</p>
              </div>
            </div>

            {/* Components Section */}
            <div className="space-y-4">
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Project Components</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Add individual components that will be part of this project (e.g., sofa, curtains, lighting)
                </p>

                {/* Add Component Form */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-medium text-gray-800 mb-2">Add New Component</h4>
                  <p className="text-xs text-gray-600 mb-4">
                    Use the Smart Wizard to specify complete product details, dimensions, fabric, and features.
                  </p>
                  <button
                    type="button"
                    onClick={startComponentNameEntry}
                    disabled={isAddingComponent}
                    className={`w-full px-6 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 ${
                      isAddingComponent 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-pink-600 hover:bg-pink-700'
                    }`}
                  >
                    {isAddingComponent ? '⏳ Preparing...' : '🎨 Add Component with Wizard'}
                  </button>
                </div>


                {/* Components List */}
                {formData.components.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-800">Added Components ({formData.components.length})</h4>
                    {formData.components.map((component) => (
                      <div key={component.id} className="bg-white border border-gray-200 rounded-lg p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="font-medium text-gray-900">{component.name}</span>
                            </div>
                            
                            {/* Taxonomy Details */}
                            {component.taxonomyData && (
                              <div className="space-y-1 text-xs text-gray-600">
                                {component.taxonomyData.product && (
                                  <div><span className="font-medium">Product:</span> {component.taxonomyData.product.label}</div>
                                )}
                                {component.taxonomyData.category && (
                                  <div><span className="font-medium">Type:</span> {component.taxonomyData.category.label}</div>
                                )}
                                {component.taxonomyData.subcategory && (
                                  <div><span className="font-medium">Style:</span> {component.taxonomyData.subcategory.label}</div>
                                )}
                                {component.taxonomyData.features && component.taxonomyData.features.length > 0 && (
                                  <div><span className="font-medium">Features:</span> {component.taxonomyData.features.map(f => f.label).join(', ')}</div>
                                )}
                                {component.taxonomyData.dimensions && Object.keys(component.taxonomyData.dimensions).length > 0 && (
                                  <div><span className="font-medium">Dimensions:</span> {Object.entries(component.taxonomyData.dimensions).map(([k,v]) => `${k}: ${v}`).join(', ')}</div>
                                )}
                                {component.taxonomyData.fabric && component.taxonomyData.fabric.description && (
                                  <div>
                                    <span className="font-medium">Fabric:</span> {component.taxonomyData.fabric.description}
                                    {component.taxonomyData.fabric.imageCount > 0 && (
                                      <span className="text-gray-500"> ({component.taxonomyData.fabric.imageCount} image{component.taxonomyData.fabric.imageCount !== 1 ? 's' : ''})</span>
                                    )}
                                  </div>
                                )}
                                {component.taxonomyData.componentPhotos && component.taxonomyData.componentPhotos.imageCount > 0 && (
                                  <div>
                                    <span className="font-medium">Component Photos:</span> {component.taxonomyData.componentPhotos.imageCount} image{component.taxonomyData.componentPhotos.imageCount !== 1 ? 's' : ''}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Image Previews */}
                            {component.taxonomyData && (
                              <div className="mt-3">
                                {console.log('Component fabric data:', component.taxonomyData.fabric)}
                                {console.log('Component photos data:', component.taxonomyData.componentPhotos)}
                                
                                {/* Fabric Images */}
                                {component.taxonomyData.fabric && component.taxonomyData.fabric.imageUrls && component.taxonomyData.fabric.imageUrls.length > 0 && (
                                  <div className="mb-2">
                                    <p className="text-xs font-medium text-gray-700 mb-1">Fabric Images:</p>
                                    <div className="flex gap-2 flex-wrap">
                                      {component.taxonomyData.fabric.imageUrls.map((url, index) => (
                                        <img
                                          key={index}
                                          src={url}
                                          alt={`Fabric ${index + 1}`}
                                          className="w-12 h-12 object-cover rounded border border-gray-300"
                                          onError={(e) => {
                                            console.error('Failed to load fabric image:', url);
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Component Photos */}
                                {component.taxonomyData.componentPhotos && component.taxonomyData.componentPhotos.imageUrls && component.taxonomyData.componentPhotos.imageUrls.length > 0 && (
                                  <div>
                                    <p className="text-xs font-medium text-gray-700 mb-1">Component Photos:</p>
                                    <div className="flex gap-2 flex-wrap">
                                      {component.taxonomyData.componentPhotos.imageUrls.map((url, index) => (
                                        <img
                                          key={index}
                                          src={url}
                                          alt={`Component ${index + 1}`}
                                          className="w-12 h-12 object-cover rounded border border-gray-300"
                                          onError={(e) => {
                                            console.error('Failed to load image:', url);
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Debug info */}
                                {process.env.NODE_ENV === 'development' && (
                                  <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                                    <p><strong>Debug:</strong></p>
                                    <p>Fabric URLs: {component.taxonomyData.fabric?.imageUrls?.length || 0}</p>
                                    <p>Component URLs: {component.taxonomyData.componentPhotos?.imageUrls?.length || 0}</p>
                                    {component.taxonomyData.fabric?.imageUrls?.length > 0 && (
                                      <p>First fabric URL: {component.taxonomyData.fabric.imageUrls[0].substring(0, 50)}...</p>
                                    )}
                                    {component.taxonomyData.componentPhotos?.imageUrls?.length > 0 && (
                                      <p>First component URL: {component.taxonomyData.componentPhotos.imageUrls[0].substring(0, 50)}...</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeComponent(component.id)}
                            className="ml-3 text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {formData.components.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm">No components added yet.</p>
                    <p className="text-xs mt-1">Components are optional - you can add them later if needed.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/projects')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 text-sm font-medium text-white bg-pink-600 border border-transparent rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Creating Project...' : 'Create Project'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Component Name Input Modal */}
      {showComponentNamePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Component Name</h3>
            
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}
            
            <div className="mb-6">
              <label htmlFor="componentName" className="block text-sm font-medium text-gray-700 mb-2">
                What are we creating today?
              </label>
              <input
                type="text"
                id="componentName"
                value={componentName}
                onChange={(e) => setComponentName(e.target.value)}
                placeholder="e.g., Living Room Sofa, Kitchen Island, Bedroom Curtains..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleComponentNameSubmit();
                  }
                }}
              />
            </div>
            
            <div className="flex justify-between">
              <button
                type="button"
                onClick={handleComponentNameCancel}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 focus:outline-none"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleComponentNameSubmit}
                disabled={isAddingComponent}
                className={`px-6 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 ${
                  isAddingComponent 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-pink-600 hover:bg-pink-700'
                }`}
              >
                {isAddingComponent ? '⏳ Creating...' : 'Continue to Wizard'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Component Smart Quote Wizard Modal */}
      {showComponentWizard && draftProjectId && currentComponentId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-auto">
            <SmartQuoteWizard
              onComplete={handleComponentWizardComplete}
              onCancel={handleComponentWizardCancel}
              projectId={draftProjectId}
              componentId={currentComponentId}
              componentName={componentName}
            />
          </div>
        </div>
      )}
    </AppLayout>
  );
}